// dashboard.client.ts - Metrics UI management and global metrics fetching

export interface GlobalMetricsData {
  totalLobbies?: string;
  totalPlayers?: string;
  totalServers?: string;
}

export interface MetricsDashboardConfig {
  updateInterval: number;
  apiEndpoint: string;
}

export interface MetricsElements {
  serverCountEl: HTMLElement | null;
  onlineCountEl: HTMLElement | null;
  lobbyCountEl: HTMLElement | null;
}

const DEFAULT_METRICS_CONFIG: MetricsDashboardConfig = {
  updateInterval: 10000,
  apiEndpoint: "/api/global-metrics",
};

let globalMetricsData: GlobalMetricsData | null = null;
let metricsUpdateInterval: ReturnType<typeof setInterval> | null = null;
let metricsConfig = DEFAULT_METRICS_CONFIG;

let metricsElements: MetricsElements = {
  serverCountEl: null,
  onlineCountEl: null,
  lobbyCountEl: null,
};

export function configureMetricsDashboard(
  config: Partial<MetricsDashboardConfig>,
): void {
  metricsConfig = { ...DEFAULT_METRICS_CONFIG, ...config };
}

export function initMetricsUI(): void {
  metricsElements.serverCountEl = document.getElementById(
    "serverCount",
  ) as HTMLElement;
  metricsElements.onlineCountEl = document.getElementById(
    "onlineCount",
  ) as HTMLElement;
  metricsElements.lobbyCountEl = document.getElementById(
    "lobbyCount",
  ) as HTMLElement;

  metricsUpdateInterval = setInterval(async () => {
    globalMetricsData = await fetchGlobalMetrics();
    updateMetricsDisplay();
  }, metricsConfig.updateInterval);

  fetchGlobalMetrics().then((data) => {
    globalMetricsData = data;
    updateMetricsDisplay();
  });
}

export async function fetchGlobalMetrics(): Promise<GlobalMetricsData | null> {
  try {
    const response = await fetch(metricsConfig.apiEndpoint);
    if (!response.ok) throw new Error("Failed to fetch global metrics");
    return await response.json();
  } catch (error) {
    console.error("Error fetching global metrics:", error);
    return null;
  }
}

export function updateMetricsDisplay(): void {
  if (!globalMetricsData) {
    return;
  }

  if (metricsElements.serverCountEl) {
    metricsElements.serverCountEl.textContent =
      globalMetricsData.totalServers || "0";
  }
  if (metricsElements.lobbyCountEl) {
    metricsElements.lobbyCountEl.textContent =
      globalMetricsData.totalLobbies || "0";
  }
  if (metricsElements.onlineCountEl) {
    metricsElements.onlineCountEl.textContent =
      globalMetricsData.totalPlayers || "0";
  }
}

export function getCurrentGlobalMetrics(): GlobalMetricsData | null {
  return globalMetricsData;
}

export async function refreshGlobalMetrics(): Promise<void> {
  globalMetricsData = await fetchGlobalMetrics();
  updateMetricsDisplay();
}

export function getMetricsDashboardStats(): {
  hasGlobalData: boolean;
  isIntervalRunning: boolean;
  config: MetricsDashboardConfig;
  elementCount: number;
} {
  const elementCount = Object.values(metricsElements).filter(
    (el) => el !== null,
  ).length;

  return {
    hasGlobalData: globalMetricsData !== null,
    isIntervalRunning: metricsUpdateInterval !== null,
    config: { ...metricsConfig },
    elementCount,
  };
}

export function cleanupMetricsDashboard(): void {
  if (metricsUpdateInterval) {
    clearInterval(metricsUpdateInterval);
    metricsUpdateInterval = null;
  }

  globalMetricsData = null;

  metricsElements = {
    serverCountEl: null,
    onlineCountEl: null,
    lobbyCountEl: null,
  };
}
